<script setup lang="ts">
import type { FetchError } from "ofetch";
import { capitalize } from "vue";

interface ErrorMap {
	title: string
	message: string
	details: string
}

const props = defineProps({
	error: {
		type: Object as PropType<FetchError | Ref<FetchError>>,
		required: true
	},
	caughtInErrorBoundary: {
		type: Boolean,
		default: false
	}
});
const emit = defineEmits(["clear-error"]);

const { t } = useI18n();

if (props.caughtInErrorBoundary) {
	const router = useRouter();
	router.afterEach(() => {
		emit("clear-error");
	});
}

const statusCodeTitleMapping = new Map<number, ErrorMap>();
statusCodeTitleMapping.set(404,
	{
		title: "404",
		message: capitalize(t("pageNotFoundMessage")),
		details: t("pageNotFoundDetails")
	}
);

const getErrorMessage = computed(() => {
	const error = toValue(props.error);

	if (error.statusCode) {
		if (!statusCodeTitleMapping.has(error.statusCode)) {
			return {
				title: error.statusCode,
				message: error.message,
				details: ""
			};
		}

		return statusCodeTitleMapping.get(error.statusCode) as ErrorMap;
	} else {
		return {
			title: "",
			message: "",
			details: ""
		};
	}
});

const navigateAndClearError = async () => {
	if (props.caughtInErrorBoundary) {
		await navigateTo("/");
	}
	emit("clear-error");
};
</script>

<template>
	<div class="box-content max-w-[476px] px-4 m-auto flex flex-col items-center justify-center">
		<div class="text-[100px] font-bold xs:text-[92px]">
			{{ getErrorMessage.title }}
		</div>
		<div class="text-2xl">
			{{ getErrorMessage.message }}
		</div>
		<div class="mt-5 text-md text-center">
			{{ getErrorMessage.details }}
		</div>
		<AppButton
			class="mt-5 max-w-[340px] w-full"
			fill
			@click="navigateAndClearError"
		>
			{{ $t('goBack') }}
		</AppButton>
	</div>
</template>
